import { useParams } from 'react-router-dom';
import { useApi } from '../classes/appApi';
import { useOffline } from '../components/App/AppOfflineContext';
import MainLayout, { useMainLayoutContext } from '../components/MainLayout';
import TrainingRecordStatus from '../components/TrainingRecordStatus';
import { TrainingRecordModel } from '../defs/defs';
import EqxCard from '../shared/components/EqxCard/EqxCard';
import EqxDocumentsPanel from '../shared/components/EqxDocumentsPanel/EqxDocumentsPanel';
import EqxError from '../shared/components/EqxError';
import EqxLoading from '../shared/components/EqxLoading';
import EqxRequestHandler from '../shared/components/EqxRequestHandler/EqxRequestHandler';
import useApiEndpoint from '../shared/hooks/ApiEndpoint';
import { formatDate } from '../shared/utils/formatDate';

const TrainingRecordDetails = ({ record }: { record: TrainingRecordModel }) => {
    return (
        <>
            <div className="row">
                <div className="col-6">
                    <dl className="dl-horizontal dl-horizontal-2-col">
                        {record.issuer && (
                            <>
                                <dt>Issuer</dt>
                                <dd>{record.issuer}</dd>
                            </>
                        )}

                        {record.date && (
                            <>
                                <dt>Certification Date</dt>
                                <dd>{formatDate(record.date)}</dd>
                            </>
                        )}

                        {record.expiryDate && (
                            <>
                                <dt>Expiry Date</dt>
                                <dd>{formatDate(record.expiryDate)}</dd>
                            </>
                        )}
                    </dl>
                </div>
                <div className="col-6">
                    <div className="col-img-container">
                        {record.logo ? (
                            <img src={record.logo} alt={`${record.name} logo`} className="img-fluid" />
                        ) : (
                            <img src="/images/placeholder-image.png" alt="Logo placeholder" className="img-fluid" />
                        )}
                    </div>
                </div>
            </div>
        </>
    );
};

// Main content component extracted from PageTrainingRecordDetail
const TrainingRecordDetailContent = () => {
    const api = useApi()
    const { id } = useParams<{ id: string }>()
    const { isOffline } = useOffline()

    const { response: record, loading, error } = useApiEndpoint<TrainingRecordModel>(
        api,
        api.endpoints.trainingRecord(id!)
    )

    const mainContext = useMainLayoutContext()
    if (!mainContext.user?.staffID) {
        if (!mainContext.user) {
            return <EqxLoading />
        } else {
            return <EqxError error="Only staff can view training record details" />;
        }
    }

    return (
        <EqxRequestHandler
            state={{ loading, error: error ?? null }}
            isEmpty={!record}
            empty={<div className="p-5 text-center text-muted">Training record not found</div>}
            content={() => {
                const rec = record!;

                return <>
                    <EqxCard
                        collapsible={false}
                        className="mb-3"
                        headerRight={
                            <TrainingRecordStatus record={rec} />
                        }
                        header={
                            <div className="d-flex align-items-center">
                                <div>
                                    <h5 className="mb-1">{rec.name}</h5>
                                </div>
                            </div>
                        }
                    >
                        <TrainingRecordDetails record={rec} />
                    </EqxCard>
                    <EqxDocumentsPanel
                        readOnly={true}
                        api={api}
                        endpointIndex={api.endpoints.trainingRecordDocuments(id!)}
                        showPreviews={!isOffline}
                        disableDownloads={isOffline}
                    />
                </>
            }}
        />
    )
}

const PageTrainingRecordDetail = () => {
    return (
        <MainLayout
            pageTitle="Training Record Details"
            relatedLinks={[
                { url: '/training-records', text: 'Back to Training Records' }
            ]}
        >
            <TrainingRecordDetailContent />
        </MainLayout>
    )
}

export default PageTrainingRecordDetail 